import { createFileRoute, notFound } from "@tanstack/react-router";
import { z } from "zod";

import { BlogPage } from "@/features/blog/components/blog-chrome";
import { useLoadInBrowser } from "@/features/blog/hooks/use-load-in-browser";
import { BlogIndex } from "@/features/blog/modules/blog-index/blog-index";
import { blogListRequest } from "@/features/blog/utils/blog-list-request";
import { BlogHero } from "@/features/blog/modules/blog-index/components/blog-hero";
import { blogListHead } from "@/features/blog/utils/blog-head";
import { reportApiOutage } from "@/lib/api-outage";
import { m } from "@/paraglide/messages.js";
import { getUrlOrigin } from "@/paraglide/runtime.js";
import { APP_LANGUAGES } from "@complete-athlete/enums";

/**
 * The blog's front page. Read on the server, so search engines get every post on the first page.
 * `?lang=de` lists only the posts written in that language.
 */
export const Route = createFileRoute("/(public)/blog/")({
  validateSearch: z.object({ lang: z.enum(APP_LANGUAGES).optional() }),
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: async ({ deps }) => {
    const { data, status } = await blogListRequest({ lang: deps.lang }).send();
    if (status === 404) throw notFound();
    // On the server a failed request hands the page to the browser, which asks again.
    if (!data) reportApiOutage();
    if (!data && typeof window !== "undefined") throw new Error(`Could not load the blog (status ${status ?? "none"})`);
    return { index: data ?? null, crumb: m.blog_title() };
  },
  head: ({ loaderData }) =>
    loaderData?.index
      ? blogListHead(loaderData.index, {
          origin: getUrlOrigin(),
          title: m.seo_blog_title(),
          description: m.blog_hero_lede_coach(),
          blogTitle: m.blog_title(),
          path: "",
        })
      : {},
  component: BlogFrontPage,
});

function BlogFrontPage() {
  const { index } = Route.useLoaderData();
  const { lang } = Route.useSearch();
  useLoadInBrowser(index);

  return (
    <BlogPage hero={<BlogHero title={m.blog_hero_title_coach()} lede={m.blog_hero_lede_coach()} />}>
      {index ? <BlogIndex index={index} filter={{ lang }} /> : null}
    </BlogPage>
  );
}
